import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquare, FileText, Calendar, Clock } from "lucide-react";

interface MobileNavigationTabsProps {
  activeTab: "chat" | "editor" | "left" | "right";
  onTabChange: (tab: "chat" | "editor" | "left" | "right") => void;
  pollType: "date" | "form" | "availability" | "quizz";
  hasPoll: boolean;
  className?: string;
}

/**
 * Barre de navigation mobile avec onglets pour basculer entre chat et éditeur
 * Affiche un badge de notification quand un poll est créé
 */
export function MobileNavigationTabs({
  activeTab,
  onTabChange,
  pollType,
  hasPoll,
  className = "",
}: MobileNavigationTabsProps) {
  const { t } = useTranslation("polls");

  // Normalisation pour le layout (ResponsiveLayout envoie "left"/"right")
  const isChat = activeTab === "chat" || activeTab === "left";
  const isEditor = activeTab === "editor" || activeTab === "right";

  // Log pour déboguer l'état des onglets
  console.log("MobileNavigationTabs - État:", {
    activeTab,
    isChat,
    isEditor,
    pollType,
    hasPoll,
  });

  // Déterminer le format de retour (si on reçoit left/right, on renvoie left/right)
  const useLayoutKeys = activeTab === "left" || activeTab === "right";

  // Déterminer l'icône et le label selon le type de poll (la couleur suit
  // toujours --primary, voir memoire ui-themes)
  const getEditorInfo = () => {
    switch (pollType) {
      case "form":
        return { icon: FileText, label: t("mobileNav.editor.form") };
      case "availability":
        return { icon: Clock, label: t("mobileNav.editor.availability") };
      case "date":
      default:
        return { icon: Calendar, label: t("mobileNav.editor.date") };
    }
  };

  const editorInfo = getEditorInfo();
  const EditorIcon = editorInfo.icon;

  // Expose the height of this bottom tab bar as a CSS var so floating widgets
  // (e.g. SupportWidget) can offset themselves above it.
  useEffect(() => {
    document.documentElement.style.setProperty("--bottom-nav-height", "48px");
    return () => {
      document.documentElement.style.setProperty("--bottom-nav-height", "0px");
    };
  }, []);

  return (
    <div
      className={`bg-background border-t border-border ${className}`}
      style={{
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <div className="flex items-center justify-around h-12 px-2">
        {/* Onglet Chat */}
        <button
          onClick={() => {
            console.log("MobileNavigationTabs - Clic onglet Chat:", {
              currentTab: activeTab,
              newTab: useLayoutKeys ? "left" : "chat",
              isChat,
              isEditor,
            });
            onTabChange(useLayoutKeys ? "left" : "chat");
          }}
          className={`flex-1 flex items-center justify-center py-3 px-4 rounded-lg transition-all relative ${
            isChat
              ? "bg-primary/20 text-primary"
              : "text-muted-foreground hover:text-foreground hover:bg-accent"
          }`}
          aria-label={t("mobileNav.showChat")}
          data-testid="mobilenavigationtabs-button"
        >
          <MessageSquare className="w-7 h-7" />
          {isChat && (
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-t-full bg-primary" />
          )}
        </button>

        {/* Onglet Éditeur (Sondage/Formulaire/Disponibilités) */}
        <button
          onClick={() => {
            console.log("MobileNavigationTabs - Clic onglet Éditeur:", {
              currentTab: activeTab,
              newTab: useLayoutKeys ? "right" : "editor",
              isChat,
              isEditor,
            });
            onTabChange(useLayoutKeys ? "right" : "editor");
          }}
          className={`flex-1 flex items-center justify-center py-3 px-4 rounded-lg transition-all relative ${
            isEditor
              ? "bg-primary/20 text-primary"
              : "text-muted-foreground hover:text-foreground hover:bg-accent"
          }`}
          aria-label={t("mobileNav.showEditor", {
            label: editorInfo.label.toLowerCase(),
          })}
          data-testid="mobilenavigationtabs-edit"
        >
          <div className="relative">
            <EditorIcon className="w-7 h-7" />
            {/* Badge de notification quand un poll est créé */}
            {hasPoll && isChat && (
              <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-primary rounded-full animate-pulse border border-background" />
            )}
          </div>
          {isEditor && (
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-t-full bg-primary" />
          )}
        </button>
      </div>
    </div>
  );
}
